import { useEffect, useState, type FormEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Link from "@mui/material/Link";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { api, setCsrfToken, unwrap } from "../api/client";
import { AuthCard } from "./AuthCard";
import { SESSION_KEY, useLogout } from "./session";

/** The key in groups of four, as authenticator apps show it. */
const grouped = (key: string) => key.match(/.{1,4}/g)?.join(" ") ?? key;

function Remember({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <FormControlLabel
      control={
        <Checkbox
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
        />
      }
      label="Remember this browser, so it doesn't ask for a code for a while"
    />
  );
}

/** First sign-in on a server: set up an authenticator app. */
export function MfaEnrolPage() {
  const qc = useQueryClient();
  const logout = useLogout();
  const [code, setCode] = useState("");
  const [remember, setRemember] = useState(false);
  const start = useMutation({
    mutationFn: () => unwrap(api.POST("/api/auth/mfa/enrol")),
  });
  const confirm = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST("/api/auth/mfa/enrol/confirm", { body: { code, remember } }),
      ),
  });
  const { mutate: begin } = start;
  useEffect(() => begin(), [begin]);

  if (confirm.data) {
    return (
      <AuthCard
        title="Recovery codes"
        subtitle="Keep these somewhere safe, away from this computer. Each one signs you in once if you lose your phone. They won't be shown again."
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          void qc.invalidateQueries({ queryKey: SESSION_KEY });
        }}
      >
        <Box
          component="ul"
          aria-label="Recovery codes"
          sx={{
            fontFamily: "monospace",
            fontSize: "1.1rem",
            columns: 2,
            m: 0,
            pl: 3,
          }}
        >
          {confirm.data.codes.map((c) => (
            <li key={c}>{c}</li>
          ))}
        </Box>
        <Button type="submit" variant="contained" size="large">
          I have saved them
        </Button>
      </AuthCard>
    );
  }

  const e = start.data;
  return (
    <AuthCard
      title="Two-factor sign-in"
      subtitle="This server asks for a code from an authenticator app (such as Microsoft or Google Authenticator) as well as your password."
      onSubmit={(ev: FormEvent) => {
        ev.preventDefault();
        confirm.mutate();
      }}
    >
      {start.isError && <Alert severity="error">{start.error.message}</Alert>}
      {e && (
        <>
          {e.qr_svg ? (
            <Box
              component="img"
              alt="QR code to scan with the authenticator app"
              src={`data:image/svg+xml;base64,${btoa(e.qr_svg)}`}
              sx={{ width: 200, alignSelf: "center" }}
            />
          ) : null}
          <Typography>
            {e.qr_svg ? "Scan the code, or add" : "In the app, add"} an account
            with this key:
          </Typography>
          <Typography
            aria-label="Key"
            sx={{
              fontFamily: "monospace",
              fontSize: "1.1rem",
              wordBreak: "normal",
            }}
          >
            {grouped(e.secret)}
          </Typography>
          <TextField
            label="Code from the app"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={(ev) => setCode(ev.target.value)}
            required
            autoFocus
          />
          <Remember checked={remember} onChange={setRemember} />
          {confirm.isError && (
            <Alert severity="error">{confirm.error.message}</Alert>
          )}
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={confirm.isPending}
          >
            Confirm
          </Button>
        </>
      )}
      <Link component="button" type="button" onClick={() => logout.mutate()}>
        Sign out
      </Link>
    </AuthCard>
  );
}

/** Later sign-ins on a server: a code from the app, or a recovery code. */
export function MfaVerifyPage() {
  const qc = useQueryClient();
  const logout = useLogout();
  const [recovery, setRecovery] = useState(false);
  const [code, setCode] = useState("");
  const [remember, setRemember] = useState(false);
  const verify = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST("/api/auth/mfa/verify", {
          body: recovery
            ? { recovery_code: code, remember }
            : { code, remember },
        }),
      ),
    onSuccess: (s) => {
      setCsrfToken(s.csrf_token);
      qc.setQueryData(SESSION_KEY, s);
    },
    onError: () => {
      setCode("");
      void qc.invalidateQueries({ queryKey: SESSION_KEY });
    },
  });
  return (
    <AuthCard
      title="Two-factor sign-in"
      subtitle={
        recovery
          ? "Enter one of your recovery codes. Each works once."
          : "Enter the code your authenticator app shows for AVGV."
      }
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        verify.mutate();
      }}
    >
      <TextField
        label={recovery ? "Recovery code" : "Code from the app"}
        inputMode={recovery ? "text" : "numeric"}
        autoComplete="one-time-code"
        value={code}
        onChange={(e) => setCode(e.target.value)}
        required
        autoFocus
      />
      <Remember checked={remember} onChange={setRemember} />
      {verify.isError && <Alert severity="error">{verify.error.message}</Alert>}
      <Button
        type="submit"
        variant="contained"
        size="large"
        disabled={verify.isPending}
      >
        Sign in
      </Button>
      <Link
        component="button"
        type="button"
        onClick={() => {
          setRecovery(!recovery);
          setCode("");
        }}
      >
        {recovery
          ? "Use the app instead"
          : "Lost your phone? Use a recovery code"}
      </Link>
      <Link component="button" type="button" onClick={() => logout.mutate()}>
        Sign out
      </Link>
    </AuthCard>
  );
}
